// Paramètres → Statuts des leads data methods. Supabase: public.set_lead_statuses / public.delete_lead_status
// (supabase/schemas/63_gavion_lead_statuses.sql §6); FakeRest (demo build, tests): the same checks and codes.
import type { SupabaseClient } from "@supabase/supabase-js";
import type { DataProvider, Identifier } from "ra-core";
import { createFakeConfigStore, isObject } from "../fakeConfigStore";
import {
  isLeadStatusMoveTarget,
  isSystemLeadStatus,
  leadStatusesFromConfigRows,
  validateLeadStatuses,
  type LeadStatusDef,
} from "../../domain/leads/leadStatusModel";

/** A refusal: `code` = the RPC's message (lead_status_in_use, …), `detail` = values or rule names. */
export class LeadStatusError extends Error {
  constructor(
    public readonly code: string,
    public readonly detail: string = "",
  ) {
    super(code);
  }
}

export type LeadStatusMethods = {
  setLeadStatuses(statuses: readonly LeadStatusDef[]): Promise<LeadStatusDef[]>;
  deleteLeadStatus(
    value: string,
    moveTo: string | null,
  ): Promise<{ moved: number }>;
  /** Every lead at that status. */
  countLeadsAtStatus(value: string): Promise<number>;
};

const toLeadStatusError = (e: { message: string; details?: string | null }) =>
  new LeadStatusError(e.message, e.details ?? "");

export const createSupabaseLeadStatusMethods = (
  getClient: () => SupabaseClient,
): LeadStatusMethods => ({
  async setLeadStatuses(statuses) {
    const { data, error } = await getClient().rpc("set_lead_statuses", {
      p_statuses: statuses,
    });
    if (error) throw toLeadStatusError(error);
    return data as LeadStatusDef[];
  },
  async deleteLeadStatus(value, moveTo) {
    const { data, error } = await getClient().rpc("delete_lead_status", {
      p_value: value,
      p_move_to: moveTo,
    });
    if (error) throw toLeadStatusError(error);
    return data as { moved: number };
  },
  async countLeadsAtStatus(value) {
    const { count, error } = await getClient()
      .from("companies")
      .select("id", { count: "exact", head: true })
      .eq("prospect_status", value);
    if (error) throw toLeadStatusError(error);
    return count ?? 0;
  },
});

const STATUS_FIELD = "company.prospect_status";

/** Mirror of private.lead_status_rule_names's condition part: a leaf on the status field whose value is, or lists, `value`,
 * or a « contient » leaf whose text is part of `value`. */
const leafMentions = (node: unknown, value: string): boolean => {
  if (Array.isArray(node)) return node.some((n) => leafMentions(n, value));
  if (!isObject(node)) return false;
  if (
    node.field === STATUS_FIELD &&
    (node.value === value ||
      (Array.isArray(node.value) && node.value.includes(value)) ||
      (node.op === "contains" &&
        typeof node.value === "string" &&
        node.value !== "" &&
        value.includes(node.value)))
  )
    return true;
  return Object.values(node).some((n) => leafMentions(n, value));
};
const ruleMentions = (
  r: { conditions?: unknown; actions?: unknown },
  value: string,
): boolean =>
  leafMentions(r.conditions, value) ||
  (Array.isArray(r.actions) &&
    r.actions.some(
      (a) =>
        isObject(a) && a.type === "set_prospect_status" && a.status === value,
    ));

export const createFakeLeadStatusMethods = (
  dp: DataProvider,
): LeadStatusMethods => {
  const { list, writeKey } = createFakeConfigStore(dp);
  const current = async () =>
    leadStatusesFromConfigRows(await list("configuration"));
  const write = (statuses: LeadStatusDef[]) =>
    writeKey("leadStatuses", statuses);
  const leadsAt = (value: string) =>
    list<{ id: Identifier; prospect_status: string }>(
      "companies",
      { prospect_status: value },
      true,
    );
  // Enabled rules only, in the SQL order (position, id); a db without the collection has no rules.
  const rulesMentioning = async (value: string) =>
    (
      await list<{
        id: number;
        position?: number;
        name: string;
        enabled?: boolean;
        conditions?: unknown;
        actions?: unknown;
      }>("automation_rules", {}, true)
    )
      .filter((r) => r.enabled !== false && ruleMentions(r, value))
      .sort((x, y) => (x.position ?? 0) - (y.position ?? 0) || x.id - y.id)
      .map((r) => r.name);
  return {
    async setLeadStatuses(statuses) {
      const issue = validateLeadStatuses(statuses);
      if (issue) throw new LeadStatusError(issue.code, issue.value);
      const values = statuses.map((s) => s.value);
      const removed = (await current())
        .map((s) => s.value)
        .filter((v) => !values.includes(v));
      const inUse: string[] = [];
      for (const v of removed) if ((await leadsAt(v)).length) inUse.push(v);
      if (inUse.length)
        throw new LeadStatusError(
          "lead_status_in_use",
          inUse.sort().join(", "),
        );
      const rules = [
        ...new Set((await Promise.all(removed.map(rulesMentioning))).flat()),
      ].sort();
      if (rules.length)
        throw new LeadStatusError("lead_status_in_rules", rules.join(", "));
      const next = statuses.map((s) => ({
        value: s.value,
        label: s.label.trim(),
        color: s.color.toLowerCase(),
      }));
      await write(next);
      return next;
    },
    async deleteLeadStatus(value, moveTo) {
      const statuses = await current();
      if (isSystemLeadStatus(value))
        throw new LeadStatusError("system_lead_status", value);
      if (!statuses.some((s) => s.value === value))
        throw new LeadStatusError("unknown_lead_status", value);
      const rules = await rulesMentioning(value);
      if (rules.length)
        throw new LeadStatusError("lead_status_in_rules", rules.join(", "));
      const moving = await leadsAt(value);
      if (
        moving.length &&
        (moveTo === null || !isLeadStatusMoveTarget(statuses, value, moveTo))
      )
        throw new LeadStatusError("invalid_move_target", moveTo ?? "null");
      for (const c of moving)
        await dp.update("companies", {
          id: c.id,
          data: { prospect_status: moveTo },
          previousData: c,
        });
      await write(statuses.filter((s) => s.value !== value));
      return { moved: moving.length };
    },
    async countLeadsAtStatus(value) {
      return (await leadsAt(value)).length;
    },
  };
};
